import { useEffect, useState } from 'react';
import { SerializableServiceInfo } from '@kemu-io/hs-types';
import { useDebouncedCallback } from 'use-debounce';
import { useHubContext } from '@common/context/HubContext/HubContext';

/**
 * Finds a service that is compatible with the given service name and version using semantic versioning.
 * Only patches and minor versions are considered compatible.
 * @param serviceName the name of the service to find
 * @param serviceVersion the version of the service to find
 * @returns an object with the service info if found, null if the was not found, and initialized: true if the service was initialized
 */
const useCompatibleHubService = (serviceName?: string, serviceVersion?: string) => {
  const { connector, services } = useHubContext();
  const [matchingService, setMatchingService] = useState<{initialized: boolean, service: SerializableServiceInfo | null}>({ initialized: false, service: null });

  const getServicesNow = useDebouncedCallback(async () => {
    if (!serviceName || !serviceVersion) { return; }

    const svSp = serviceVersion.split('.');
    // Caret versioning only works if the major version is not 0, so we use >= for 0.x.x versions.
    const compareMethod = svSp[0] === '0' ? '>=' : '^';
    const found = await connector.getCompatibleService(serviceName, serviceVersion, compareMethod);
    setMatchingService({ initialized: true, service: found || null });

  }, 100, { leading: true });

  useEffect(() => {
    getServicesNow();

    return () => {
      getServicesNow.cancel();
    };
  }, [
    serviceName, serviceVersion,
    services, // < == force checking services whenever the services list changes
    getServicesNow]);

  return matchingService;
};

export default useCompatibleHubService;
